/**
 * Bloc W — Nécessité d'une supervision renforcée ?
 * - Risques élevés (auto-cochés si permis correspondant existe)
 * - Types d'opérations à risque élevé (sélectionnables manuellement)
 * - OUI/NON auto en bandeau rouge si ≥ 1 case cochée
 * - Champs présence donneur d'ordre Lafarge + mesures prévention
 * - Footer rouge avertissement électrique
 */
import { useMemo } from 'react';

const RISQUES_ELEVES = [
  { id: 'espace_confine', label: 'Espace confiné', auto_permit: 'permis_espace_confine' },
  { id: 'travail_hauteur', label: 'Travail en hauteur (yc trappes)', auto_permit: 'permis_hauteur' },
  { id: 'levage_manutention', label: 'levage/manutention', auto_permit: 'permis_levage' },
  { id: 'points_chauds', label: 'Points chauds', auto_permit: 'permis_feu' },
  { id: 'enceinte_metallique', label: 'Enceinte métallique', auto_permit: null },
  { id: 'autres_risques', label: 'Autres', auto_permit: null },
];

const TYPES_OPS = [
  { id: 'nouvelle_peu_freq', label: 'Nouvelle ou peu fréquente' },
  { id: 'nouvelle_intervenant', label: "Nouvelle pour l'intervenant/l'entreprise" },
  { id: 'avec_coactivite', label: 'Avec co-activité' },
  { id: 'levage_complexe', label: 'Levage complexe' },
  { id: 'bordure_eau', label: "En bordure d'eau ou front de taille" },
  { id: 'autres_ops', label: 'Autres' },
];

function RiskGauge() {
  // SVG half-circle gauge with RISK label
  return (
    <svg viewBox="0 0 200 160" className="w-full h-full">
      <defs>
        <linearGradient id="riskg" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#22c55e" />
          <stop offset="33%" stopColor="#facc15" />
          <stop offset="66%" stopColor="#fb923c" />
          <stop offset="100%" stopColor="#dc2626" />
        </linearGradient>
      </defs>
      <path d="M 20 110 A 80 80 0 0 1 180 110" stroke="url(#riskg)" strokeWidth="20" fill="none" strokeLinecap="round" />
      {/* needle */}
      <line x1="100" y1="110" x2="160" y2="60" stroke="#09090B" strokeWidth="4" strokeLinecap="round" />
      <circle cx="100" cy="110" r="6" fill="#09090B" />
      <text x="100" y="148" textAnchor="middle" fontFamily="Chivo, sans-serif" fontWeight="900" fontSize="22" fill="#09090B" letterSpacing="2">RISK</text>
    </svg>
  );
}

export default function BlocW({ value, onChange, linkedPermitTypes = [] }) {
  const v = value || {};
  const risques = v.risques || {};
  const ops = v.ops || {};

  // Auto-check risques élevés if linkedPermitTypes contains the auto_permit
  const computedRisques = useMemo(() => {
    const out = { ...risques };
    RISQUES_ELEVES.forEach((r) => {
      if (r.auto_permit && linkedPermitTypes.includes(r.auto_permit)) {
        out[r.id] = true;
      }
    });
    return out;
  }, [risques, linkedPermitTypes]);

  // Supervision renforcée requise SEULEMENT si AU MOINS 1 risque élevé ET 1 type d'opération à risque élevé sont cochés
  const anyRisqueChecked = Object.values(computedRisques).some(Boolean);
  const anyOpChecked = Object.values(ops).some(Boolean);
  const anyChecked = anyRisqueChecked && anyOpChecked;

  const toggle = (group, id) => {
    const next = { ...v, [group]: { ...(v[group] || {}), [id]: !(v[group] || {})[id] } };
    onChange(next);
  };

  const setField = (k, val) => onChange({ ...v, [k]: val });

  return (
    <div className="border-2 border-stone-300">
      {/* Header */}
      <div
        data-testid="bloc-w-header"
        className={`px-4 py-3 flex items-center justify-between transition-all ${
          anyChecked ? 'bg-[#DA291C] text-white font-bold' : 'bg-stone-500 text-white'
        }`}
      >
        <div className="text-[11px] font-bold uppercase tracking-wider">W —</div>
        <div className={`text-lg ${anyChecked ? 'font-black uppercase tracking-wide' : 'font-semibold'}`}>
          Nécessité d'une supervision renforcée ?
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-bold uppercase tracking-wider">OUI</span>
          <div className={`w-5 h-5 border-2 flex items-center justify-center ${
            anyChecked ? 'border-white bg-white' : 'border-white bg-transparent'
          }`}>
            {anyChecked && <span className="text-[#DA291C] font-black">✓</span>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 p-4 bg-white">
        {/* Risques élevés (auto) */}
        <div className="lg:col-span-3">
          <div className="text-sm font-bold mb-2">Risques élevés</div>
          <div className="space-y-2">
            {RISQUES_ELEVES.map((r) => (
              <label key={r.id} className="flex items-center gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  data-testid={`bloc-w-risque-${r.id}`}
                  checked={!!computedRisques[r.id]}
                  onChange={() => toggle('risques', r.id)}
                  disabled={r.auto_permit && linkedPermitTypes.includes(r.auto_permit)}
                  className="w-4 h-4 accent-[#DA291C]"
                />
                <span className="text-stone-800">{r.label}</span>
                {r.auto_permit && linkedPermitTypes.includes(r.auto_permit) && (
                  <span className="text-[9px] uppercase font-bold text-[#DA291C]">(auto)</span>
                )}
              </label>
            ))}
          </div>
        </div>

        {/* Gauge */}
        <div className="lg:col-span-3 flex items-center justify-center">
          {anyChecked ? (
            <div className="w-full max-w-[180px]" data-testid="bloc-w-gauge">
              <RiskGauge />
            </div>
          ) : (
            <div className="text-[11px] text-stone-400 italic text-center">
              Aucune supervision renforcée requise pour le moment
            </div>
          )}
        </div>

        {/* Types d'opérations à risque élevé */}
        <div className="lg:col-span-3">
          <div className="text-sm font-bold mb-2">Types d'opérations à risque élevé</div>
          <div className="space-y-2">
            {TYPES_OPS.map((o) => (
              <label key={o.id} className="flex items-center gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  data-testid={`bloc-w-op-${o.id}`}
                  checked={!!ops[o.id]}
                  onChange={() => toggle('ops', o.id)}
                  className="w-4 h-4 accent-[#DA291C]"
                />
                <span className="text-stone-800">{o.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Présence donneur d'ordre Lafarge */}
        <div className="lg:col-span-3 border-l border-stone-200 pl-4">
          <div className={`text-sm font-bold mb-2 ${anyChecked ? 'text-[#DA291C]' : ''}`}>
            Présence obligatoire du donneur d'ordre Lafarge
          </div>
          <div className="space-y-2 text-xs">
            <div>
              <label className="label-industrial">Date prévisible</label>
              <input type="date" data-testid="bloc-w-date"
                value={v.date_prev || ''} onChange={(e) => setField('date_prev', e.target.value)}
                className="w-full px-2 py-1.5 border border-stone-300 bg-amber-50 outline-none focus:border-[#09090B]" />
            </div>
            <div>
              <label className="label-industrial">Durée prévisible (heures)</label>
              <input type="number" data-testid="bloc-w-duree"
                value={v.duree_h || ''} onChange={(e) => setField('duree_h', e.target.value)}
                className="w-full px-2 py-1.5 border border-stone-300 bg-amber-50 outline-none focus:border-[#09090B]" />
            </div>
            <div className="flex gap-3 mt-2">
              {['demarrage', 'pendant', 'specifique'].map((m) => (
                <label key={m} className="flex items-center gap-1 capitalize cursor-pointer">
                  <input type="checkbox"
                    data-testid={`bloc-w-presence-${m}`}
                    checked={!!v[`presence_${m}`]}
                    onChange={(e) => setField(`presence_${m}`, e.target.checked)}
                    className="w-3.5 h-3.5 accent-[#DA291C]" />
                  <span>{m}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="mt-3">
            <div className={`text-xs font-bold mb-1 ${anyChecked ? 'text-white bg-[#DA291C] px-2 py-1' : 'text-stone-700'}`}>
              Le donneur d'ordre Lafarge vérifiera que toutes les mesures de prévention sont en place
            </div>
            <textarea rows={3}
              data-testid="bloc-w-mesures"
              value={v.mesures_donneur || ''}
              onChange={(e) => setField('mesures_donneur', e.target.value)}
              className="w-full px-2 py-1.5 border border-stone-300 bg-amber-50 outline-none focus:border-[#09090B] text-xs resize-y" />
          </div>
        </div>
      </div>

      {/* Footer rouge avertissement */}
      <div className="border-t-2 border-red-300 bg-red-50 p-3">
        <p className="text-xs text-red-700 font-semibold leading-relaxed">
          Pour tout travaux d'ordre électrique réalisés par une entreprise extérieure, la deuxième étape
          de la consignation doit être réalisée par l'entreprise extérieure.
          <strong> (Attestation de consignation séparée obligatoire)</strong>
        </p>
      </div>
    </div>
  );
}
